import { useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { FileText, Mail } from 'lucide-react';

interface SendIntakeFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  appointment: {
    id: string;
    name: string;
    email: string;
    service_type: string;
    client_id: string | null;
  } | null;
  onSent?: () => void;
}

export default function SendIntakeFormDialog({
  open,
  onOpenChange,
  appointment,
  onSent,
}: SendIntakeFormDialogProps) {
  const { toast } = useToast();
  const [message, setMessage] = useState('');
  const [isSending, setIsSending] = useState(false);

  const handleSend = async () => {
    if (!appointment) return;

    if (!appointment.client_id) {
      toast({
        title: 'Error',
        description: 'This appointment is not linked to a client record.',
        variant: 'destructive',
      });
      return;
    }

    setIsSending(true);
    try {
      // Create intake form linked to appointment and client
      const { data: intakeForm, error: formError } = await supabase
        .from('intake_forms')
        .insert({
          client_id: appointment.client_id,
          appointment_id: appointment.id,
          status: 'sent',
          sent_at: new Date().toISOString(),
          form_data: {
            appointment_service: appointment.service_type,
            custom_message: message || null,
          },
        })
        .select()
        .single();

      if (formError) throw formError;

      toast({
        title: 'Intake Form Sent',
        description: `Intake form has been sent to ${appointment.email}`,
      });

      setMessage('');
      onOpenChange(false);
      onSent?.();
    } catch (error) {
      console.error('Error sending intake form:', error);
      toast({
        title: 'Error',
        description: 'Failed to send intake form.',
        variant: 'destructive',
      });
    } finally {
      setIsSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5 text-primary" />
            Send Intake Form
          </DialogTitle>
          <DialogDescription>
            Send an intake form to the client for this appointment.
          </DialogDescription>
        </DialogHeader>
        
        {appointment && (
          <div className="space-y-4 py-4">
            <div className="space-y-2 p-3 bg-muted rounded-lg">
              <div className="text-sm">
                <span className="font-semibold">Client:</span> {appointment.name}
              </div>
              <div className="text-sm flex items-center gap-1">
                <Mail className="h-3 w-3" />
                <span className="font-semibold">Email:</span> {appointment.email}
              </div>
              <div className="text-sm">
                <span className="font-semibold">Service:</span> {appointment.service_type}
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="message">Custom Message (Optional)</Label>
              <Textarea
                id="message"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Add a personalized message for the client..."
                rows={3}
              />
              <p className="text-xs text-muted-foreground">
                This message will be included with the intake form email.
              </p>
            </div>
          </div>
        )}

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isSending}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSend}
            disabled={isSending}
            className="bg-primary"
          >
            {isSending ? 'Sending...' : 'Send Intake Form'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
